<script lang="ts" setup>
withDefaults(
  defineProps<{
    title: string;
    active?: boolean;
    icon?: string;
    to?: string;
    isSubItem?: boolean;
    /** Parent row that expands a submenu — shows a chevron affordance. */
    isGroup?: boolean;
    /** Whether the submenu group is currently open. */
    expanded?: boolean;
    badge?: boolean;
    /** Icon-only drawer: hide label chrome and show a tooltip instead. */
    rail?: boolean;
    /** Set false when a parent menu/flyout already conveys the label. */
    tooltip?: boolean;
  }>(),
  {
    tooltip: true,
  },
);
</script>

<template>
  <v-list-item
    :title="rail ? undefined : title"
    rounded="lg"
    active-class="sidebar-item--active"
    density="compact"
    :active="active"
    :to="to"
    :class="[
      'sidebar-item',
      {
        'sidebar-item--sub': isSubItem,
        'sidebar-item--group': isGroup,
        'sidebar-item--group-open': isGroup && expanded,
        'sidebar-item--rail': rail,
      },
    ]"
    min-height="36"
  >
    <v-tooltip
      v-if="rail && tooltip"
      activator="parent"
      location="end"
      :open-delay="200"
    >
      {{ title }}
    </v-tooltip>
    <template v-slot:prepend>
      <v-icon
        v-if="icon"
        size="20"
        :class="['sidebar-item__icon', { 'sidebar-item__icon--active': active }]"
      >
        {{ icon }}
      </v-icon>
    </template>
    <template
      v-if="!rail && (isGroup || badge)"
      v-slot:append
    >
      <v-icon
        v-if="isGroup"
        size="18"
        class="sidebar-item__chevron"
        :class="{ 'sidebar-item__chevron--open': expanded }"
      >
        mdi-chevron-down
      </v-icon>
      <span
        v-else
        class="sidebar-item__badge-dot"
      />
    </template>
    <template
      v-else-if="rail && badge"
      v-slot:append
    >
      <span class="sidebar-item__badge-dot sidebar-item__badge-dot--rail" />
    </template>
  </v-list-item>
</template>

<style lang="scss" scoped>
// DS dark surface: label #ebeae8 on #21201e, icons at secondary #a6a39f.
// Active row is a solid brand fill with near-black text — the same treatment the
// POS bottom-nav uses for the selected tab.
.sidebar-item {
  min-height: 36px !important;
  margin-bottom: 2px;
  padding-block: 0 !important;

  :deep(.v-list-item__content) {
    padding-left: 0;
    padding-block: 0 !important;
  }

  // Vuetify inserts a 32px spacer after the prepend icon; 10px matches the DS
  // icon-to-label gap.
  :deep(.v-list-item__spacer) {
    width: 10px !important;
  }

  :deep(.v-list-item-title) {
    font-size: 14px;
    font-weight: 500;
    color: #ebeae8;
    line-height: 20px;
  }

  :deep(.v-list-item__overlay) {
    display: none;
  }

  &:hover:not(.sidebar-item--active) {
    background-color: #302f2c;
  }

  &__icon {
    color: #a6a39f;
    opacity: 1;
  }

  &__badge-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: rgb(var(--v-theme-error));
    flex-shrink: 0;

    &--rail {
      position: absolute;
      top: 6px;
      right: 10px;
    }
  }

  &--rail {
    // VListItem defaults to CSS grid with a 1fr content column — that keeps the
    // icon left-aligned even when the title is hidden. Switch to flex centering.
    position: relative;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    padding-inline: 0 !important;
    grid-template-columns: none !important;

    :deep(.v-list-item__content),
    :deep(.v-list-item__spacer) {
      display: none !important;
    }

    :deep(.v-list-item__prepend) {
      margin-inline: 0 !important;
      grid-area: unset !important;
    }

    // Keep badge dots, but collapse append so it doesn't offset the icon.
    :deep(.v-list-item__append) {
      position: absolute;
      inset: 0;
      width: 0 !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible;
      grid-area: unset !important;
    }
  }

  &__chevron {
    color: #75736f;
    opacity: 1;
    transition:
      transform 0.2s ease,
      color 0.15s ease;

    &--open {
      transform: rotate(-180deg);
      color: #a6a39f;
    }
  }

  &--active {
    background-color: rgb(var(--v-theme-primary));

    :deep(.v-list-item-title) {
      color: #fff;
      font-weight: 600;
    }

    .sidebar-item__icon {
      color: #fff;
    }

    .sidebar-item__chevron {
      color: #fff;
    }
  }

  // Expandable parents: slightly heavier label so groups read as folders,
  // not as destination links.
  &--group {
    :deep(.v-list-item-title) {
      font-weight: 600;
    }
  }

  // Sub-items: one step smaller, indented under the parent group.
  &--sub {
    --indent-padding: 22px !important;
    min-height: 34px !important;

    :deep(.v-list-item-title) {
      font-size: 13px;
      font-weight: 400;
    }

    .sidebar-item__icon {
      font-size: 18px !important;
    }
  }
}
</style>
